import { useNavigate } from "react-router-dom";
import {
  RiFileTextLine,
  RiMoneyDollarCircleLine,
  RiHistoryLine,
  RiPrinterLine,
  RiDownloadLine,
  RiMailLine,
  RiUserLine,
} from "react-icons/ri";

const Billing = () => {
  const navigate = useNavigate();

  const invoicingTools = [
    {
      id: "create-invoice",
      name: "Create Invoice",
      description: "Generate professional invoices for your clients",
      icon: RiFileTextLine,
      path: "/billing/create-invoice",
      color: "from-violet-500 to-purple-500",
      stats: "Quick Generate",
    },
    {
      id: "invoice-templates",
      name: "Invoice Templates",
      description: "Customize and manage invoice templates",
      icon: RiPrinterLine,
      path: "/billing/templates",
      color: "from-blue-500 to-cyan-500",
      stats: "Multiple Designs",
    },
    {
      id: "recurring-invoices",
      name: "Recurring Invoices",
      description: "Set up automated recurring invoices",
      icon: RiDownloadLine,
      path: "/billing/recurring",
      color: "from-green-500 to-emerald-500",
      stats: "Auto Billing",
    },
  ];

  const paymentTools = [
    {
      id: "payment-collection",
      name: "Payment Collection",
      description: "Collect payments through multiple payment gateways",
      icon: RiMoneyDollarCircleLine,
      path: "/billing/payments",
      color: "from-yellow-500 to-orange-500",
      stats: "Secure Payments",
    },
    {
      id: "payment-reminders",
      name: "Payment Reminders",
      description: "Automate payment reminders and follow-ups",
      icon: RiMailLine,
      path: "/billing/reminders",
      color: "from-pink-500 to-rose-500",
      stats: "Auto Reminders",
    },
  ];

  const reportingTools = [
    {
      id: "invoice-history",
      name: "Invoice History",
      description: "View and manage all your invoices",
      icon: RiHistoryLine,
      path: "/billing/history",
      color: "from-teal-500 to-cyan-500",
      stats: "Track Invoices",
    },
    {
      id: "financial-reports",
      name: "Financial Reports",
      description: "Generate detailed financial reports and analytics",
      icon: RiFileTextLine,
      path: "/billing/reports",
      color: "from-indigo-500 to-purple-500",
      stats: "Business Insights",
    },
  ];

  const customerTools = [
    {
      id: "customer-portal",
      name: "Customer Portal",
      description: "Access customer dashboard and manage account",
      icon: RiUserLine,
      path: "/billing/customer-portal",
      color: "from-emerald-500 to-teal-500",
      stats: "Account Management",
    },
  ];

  const renderToolSection = (tools, title) => (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-secondary-900">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((tool) => (
          <div
            key={tool.id}
            className="card group cursor-pointer hover:shadow-lg"
            onClick={() => navigate(tool.path)}
          >
            <div className="flex items-start space-x-4">
              <div
                className={`p-4 rounded-xl bg-gradient-to-br ${tool.color} text-white`}
              >
                <tool.icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-secondary-900 group-hover:text-primary-600">
                  {tool.name}
                </h3>
                <p className="text-secondary-600 mt-1">{tool.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-secondary-500">
                    {tool.stats}
                  </span>
                  <span className="text-primary-600 text-sm font-medium group-hover:underline">
                    Open →
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">
          Billing & Invoicing
        </h1>
        <p className="mt-2 text-secondary-600">
          Manage invoices, payments, and financial reports
        </p>
      </div>

      {renderToolSection(invoicingTools, "Invoicing Tools")}
      {renderToolSection(paymentTools, "Payment Management")}
      {renderToolSection(reportingTools, "Reports & History")}

      <div>
        <h2 className="text-xl font-semibold mb-4">Customer Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customerTools.map((tool) => (
            <div
              key={tool.id}
              className="card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(tool.path)}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center`}
                >
                  <tool.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{tool.name}</h3>
                  <p className="text-gray-500">{tool.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-gray-500">{tool.stats}</span>
                <span className="text-primary-600">Open →</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card bg-gradient-to-br from-primary-50 to-primary-100/50 border-2 border-dashed border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-primary-900">
              Need Help with Billing?
            </h2>
            <p className="mt-1 text-primary-700">
              Learn how to manage invoices and payments effectively
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              /* Help documentation link */
            }}
          >
            View Guide
          </button>
        </div>
      </div>

      {/* Quick Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card bg-green-50">
          <h3 className="text-lg font-semibold text-green-800">
            Total Revenue
          </h3>
          <p className="text-2xl font-bold text-green-600 mt-2">$24,500.00</p>
          <p className="text-sm text-green-600 mt-1">+12.5% from last month</p>
        </div>
        <div className="card bg-blue-50">
          <h3 className="text-lg font-semibold text-blue-800">
            Pending Invoices
          </h3>
          <p className="text-2xl font-bold text-blue-600 mt-2">12</p>
          <p className="text-sm text-blue-600 mt-1">$5,240.00 outstanding</p>
        </div>
        <div className="card bg-purple-50">
          <h3 className="text-lg font-semibold text-purple-800">
            Paid This Month
          </h3>
          <p className="text-2xl font-bold text-purple-600 mt-2">28</p>
          <p className="text-sm text-purple-600 mt-1">invoices completed</p>
        </div>
      </div>
    </div>
  );
};

export default Billing;
